import { Button, Grid } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import CKEditor from 'ckeditor4-react';
import { Labeled } from 'react-admin';
import { indexOf } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import PreviewEmailApi from '../../../../core/apis/PreviewEmailApi';
import DialogTitleWithCloseButton from '../../../../core/components/DialogTitleWithCloseButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import RichTextInput from '../../../../core/components/inputs/RichTextInput';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';

/**
 * Email preview dialog
 */
const EmailPreviewDialog = ({
  showDialog,
  formData,
  handleDialogToggle,
  resourceuploadId
}) => {
  // Unsubstituted subject and body come from formData
  const { subject, msgBody, selectedItems } = formData;
  // Current Distribution list item we are previewing
  const [currentItem, setCurrentItem] = useState(0);
  const [substitutedEmail, setSubstitutedEmail] = useState({});
  const notifyServerError = useNotifyServerError();
  const item = selectedItems[currentItem];
  const [loading, setLoading] = useState(false);

  // Go to next/previous item
  const nextItem = useCallback(() => {
    if (currentItem + 1 >= selectedItems.length) {
      // Loop to position 0
      setCurrentItem(0);
    } else {
      setCurrentItem(currentItem + 1);
    }
  }, [selectedItems, currentItem]);

  const previousItem = useCallback(() => {
    if (currentItem - 1 < 0) {
      // Loop to last item
      setCurrentItem(selectedItems.length - 1);
    } else {
      setCurrentItem(currentItem - 1);
    }
  }, [selectedItems, currentItem]);

  const fetchData = useCallback(() => {
    if (item) {
      const emailToSubstitute = {
        subject,
        msgBody,
        fundId: item.fundId,
        investorId: item.investorId,
        resourceUploadId: resourceuploadId,
        documentId: item.documentId,
        userId: item.userId,
        userEmail: item.email,
        testEmail: false
      };
      setLoading(true);
      PreviewEmailApi.deliveryTabPreview(emailToSubstitute)
        .then(data => {
          setSubstitutedEmail(data);
        })
        .catch(notifyServerError)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [item, msgBody, notifyServerError, resourceuploadId, subject]);

  useEffect(() => {
    if (showDialog) {
      fetchData();
    }
  }, [fetchData, showDialog]);

  return (
    <Dialog
      fullWidth
      open={showDialog}
      aria-label="Preview Email"
      maxWidth="md"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleDialogToggle();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleDialogToggle}>
        Preview Email
      </DialogTitleWithCloseButton>
      <DialogContent>
        <Grid container>
          {selectedItems.length === 0 ? (
            <span>
              No contacts are selected. Please select some contacts before see
              the Preview email.
            </span>
          ) : (
            <>
              <Grid item xs={12}>
                <TextField
                  value={substitutedEmail.subject}
                  label="Subject:"
                  disabled
                />
              </Grid>

              <Grid item xs={12}>
                <Labeled label="Body:">
                  <CKEditor
                    data={substitutedEmail.msgBody}
                    readOnly
                    id="email-body"
                    config={RichTextInput.defaultProps.editorConfig}
                  />
                </Labeled>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      {selectedItems.length === 0 ? (
        <></>
      ) : (
        <DialogActions>
          <div style={{ flexGrow: 1 }}>
            {`${item.firstname} ${item.lastname} / ${item.investorName}`}
          </div>
          {loading && <CircularProgress size={25} thickness={2} />}
          {`Contact: ${indexOf(selectedItems, item) + 1} of ${
            selectedItems.length
          }`}
          <Button onClick={previousItem}>Previous</Button>
          <Button onClick={nextItem}>Next</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default EmailPreviewDialog;
